/**
 * Pure derivation helpers for the Command Center.
 * Every function takes raw API data and returns derived signals — no fetching,
 * no React. Reused across multiple tabs.
 */
import type {
  AdsCampaign,
  AdsDashboard,
  ChannelGroupRow,
  CommandCenter,
  ContentPageRow,
  SeoQueryRow,
} from '@/services/marketingIntegrations.service';

export type ActionTag = 'SCALE' | 'KEEP' | 'CUT' | 'FIX';

export interface CampaignWithAction extends AdsCampaign {
  action: ActionTag;
  reason: string;
  wastedSpend: number;
}

/**
 * Action tag rules:
 *  - CUT: spent ≥$1k AND (ROAS < 1× or zero conversions)
 *  - FIX: CPA ≥3× target OR spend ≥$1k with 1-3 conversions and CPA > 2× target
 *  - SCALE: ROAS ≥5× and spend ≥$500 (proven, room to scale)
 *  - KEEP: anything else with conversions
 */
export function tagCampaign(c: AdsCampaign, targetCpa: number): CampaignWithAction {
  const cpa = c.cpa ?? Infinity;
  const roas = c.roas ?? 0;
  const spend = c.spend ?? 0;
  const conv = c.conversions ?? 0;

  let action: ActionTag = 'KEEP';
  let reason = 'Performing within bounds.';
  let wastedSpend = 0;

  if (spend >= 1000 && (conv === 0 || (roas > 0 && roas < 1))) {
    action = 'CUT';
    reason = conv === 0
      ? `${formatMoney(spend)} spent, 0 conversions.`
      : `ROAS ${roas.toFixed(1)}× — burning money.`;
    wastedSpend = spend;
  } else if (cpa >= targetCpa * 3) {
    action = 'FIX';
    reason = `CPA ${formatMoney(cpa)} is ${(cpa / targetCpa).toFixed(1)}× over target.`;
    wastedSpend = Math.max(0, spend - conv * targetCpa);
  } else if (spend >= 1000 && conv > 0 && conv <= 3 && cpa > targetCpa * 2) {
    action = 'FIX';
    reason = `Only ${conv} conv. on ${formatMoney(spend)} — needs targeting fix.`;
    wastedSpend = Math.max(0, spend - conv * targetCpa);
  } else if (roas >= 5 && spend >= 500) {
    action = 'SCALE';
    reason = `${roas.toFixed(1)}× ROAS at ${formatMoney(spend)} — has headroom.`;
  } else if (conv > 0 && cpa <= targetCpa * 1.2) {
    action = 'KEEP';
    reason = `${conv} conv. at ${formatMoney(cpa)} CPA — on target.`;
  }

  return { ...c, action, reason, wastedSpend };
}

export interface ChannelSummary {
  channel: string;
  spend: number;
  conversions: number;
  cpa: number | null;
  action: ActionTag;
  reason: string;
}

export function summarizeChannels(d: AdsDashboard, targetCpa: number): ChannelSummary[] {
  const out: ChannelSummary[] = [];
  if (d.google_ads?.configured) {
    const cpa = d.google_ads.conversions > 0 ? d.google_ads.spend / d.google_ads.conversions : null;
    out.push({
      channel: 'Google Ads',
      spend: d.google_ads.spend,
      conversions: d.google_ads.conversions,
      cpa,
      ...channelAction(cpa, d.google_ads.conversions, d.google_ads.spend, targetCpa),
    });
  }
  if (d.meta_ads?.configured) {
    const cpa = d.meta_ads.conversions > 0 ? d.meta_ads.spend / d.meta_ads.conversions : null;
    out.push({
      channel: 'Meta Ads',
      spend: d.meta_ads.spend,
      conversions: d.meta_ads.conversions,
      cpa,
      ...channelAction(cpa, d.meta_ads.conversions, d.meta_ads.spend, targetCpa),
    });
  }
  return out;
}

function channelAction(cpa: number | null, conv: number, spend: number, target: number): { action: ActionTag; reason: string } {
  if (spend >= 1000 && conv === 0) return { action: 'CUT', reason: `${formatMoney(spend)} spent, 0 conv.` };
  if (cpa == null) return { action: 'KEEP', reason: 'Not enough data yet.' };
  if (cpa >= target * 3) return { action: 'FIX', reason: `CPA ${formatMoney(cpa)} is ${(cpa / target).toFixed(1)}× target.` };
  if (cpa <= target * 0.6 && conv >= 5) return { action: 'SCALE', reason: `CPA ${formatMoney(cpa)} well under target.` };
  return { action: 'KEEP', reason: `CPA ${formatMoney(cpa)} on track.` };
}

// ─── SEO query intent classification ────────────────────────────────────────

export type Intent = 'product' | 'comparison' | 'educational' | 'navigational' | 'informational';

export function classifyQueryIntent(query: string): Intent {
  const q = query.toLowerCase();
  // Comparison / review intent — "vs", "alternative", "review", "best", "top X"
  if (/\b(vs\.?|versus|compare|comparison|alternative|review|reviews|best|top \d|top|cheaper|better than)\b/.test(q)) return 'comparison';
  // Educational / how-to — instructional language
  if (/\b(how to|how do|what is|why|guide|tutorial|tips?|examples?|step|steps?|learn|explain|definition)\b/.test(q)) return 'educational';
  // Navigational — brand or domain-style queries
  if (/\b(login|sign[- ]?in|dashboard|app|download|contact|support|customer service)\b/.test(q) || /\.(com|io|co|ai)\b/.test(q)) return 'navigational';
  // Product / commercial — pricing, buy, signup, near me, service nouns
  if (/\b(price|pricing|cost|fees?|buy|purchase|order|signup|sign[- ]?up|free trial|demo|near me|book|appointment|service)\b/.test(q)) return 'product';
  // Default fallback
  return 'informational';
}

export interface IntentBucket {
  intent: Intent;
  label: string;
  queries: SeoQueryRow[];
  totalImpressions: number;
  totalClicks: number;
  avgCtr: number;
  avgPosition: number;
}

const INTENT_LABELS: Record<Intent, string> = {
  product: 'Product / Service',
  comparison: 'Comparison / Review',
  educational: 'Educational / How-to',
  navigational: 'Navigational',
  informational: 'Informational',
};

export function bucketQueriesByIntent(queries: SeoQueryRow[]): IntentBucket[] {
  const groups = new Map<Intent, SeoQueryRow[]>();
  for (const q of queries) {
    const intent = classifyQueryIntent(q.query);
    const arr = groups.get(intent) ?? [];
    arr.push(q);
    groups.set(intent, arr);
  }
  const out: IntentBucket[] = [];
  for (const intent of ['product', 'comparison', 'educational', 'navigational', 'informational'] as Intent[]) {
    const qs = groups.get(intent) ?? [];
    if (qs.length === 0) continue;
    const totalImpressions = qs.reduce((s, q) => s + q.impressions, 0);
    const totalClicks = qs.reduce((s, q) => s + q.clicks, 0);
    const weightedPos = qs.reduce((s, q) => s + q.position * q.impressions, 0);
    out.push({
      intent,
      label: INTENT_LABELS[intent],
      queries: qs,
      totalImpressions,
      totalClicks,
      avgCtr: totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0,
      avgPosition: totalImpressions > 0 ? weightedPos / totalImpressions : 0,
    });
  }
  return out;
}

// ─── Findings: highest-leverage opportunities ──────────────────────────────

export type FindingTone = 'good' | 'warn' | 'bad';

export interface Finding {
  tone: FindingTone;
  title: string;
  body: string;
  metric: string;
}

/**
 * Surfaces 5 (or fewer) highest-leverage findings from raw data. Every
 * finding references a specific number — no generic platitudes.
 */
export function deriveFindings(cc: CommandCenter, targetCpa = 200): Finding[] {
  const findings: Finding[] = [];
  const campaigns = cc.campaigns?.rows ?? [];
  const tagged = campaigns.map((c) => tagCampaign(c, targetCpa));

  // 1. Biggest waste — single worst campaign
  const wasteSorted = [...tagged].sort((a, b) => b.wastedSpend - a.wastedSpend);
  const worst = wasteSorted[0];
  if (worst && worst.wastedSpend > 500) {
    findings.push({
      tone: 'bad',
      title: 'Pause to stop the bleed',
      body: `${worst.name} (${worst.channel}) is wasting ${formatMoney(worst.wastedSpend)} this period — ${worst.reason}`,
      metric: formatMoney(worst.wastedSpend),
    });
  }

  // 2. Best campaign with headroom — scale opportunity
  const scaleCandidate = tagged.filter((c) => c.action === 'SCALE')
    .sort((a, b) => (b.roas ?? 0) - (a.roas ?? 0))[0];
  if (scaleCandidate) {
    findings.push({
      tone: 'good',
      title: 'Scale your winner',
      body: `${scaleCandidate.name} is running at ${(scaleCandidate.roas ?? 0).toFixed(1)}× ROAS on ${formatMoney(scaleCandidate.spend)} — double the budget.`,
      metric: `${(scaleCandidate.roas ?? 0).toFixed(1)}×`,
    });
  }

  // 3. Highest-traffic page with zero conversions — broken funnel
  const pages = cc.contentPages?.rows ?? [];
  const broken = [...pages].filter((p) => p.sessions >= 500 && p.conversions === 0)
    .sort((a, b) => b.sessions - a.sessions)[0];
  if (broken) {
    findings.push({
      tone: 'warn',
      title: 'Broken conversion path',
      body: `${broken.path} attracted ${broken.sessions.toLocaleString()} sessions but converted 0. Add a CTA or measurement event.`,
      metric: `${broken.sessions.toLocaleString()} sessions`,
    });
  }

  // 4. Page-1 SEO with low CTR — title-tag fix
  const queries = cc.gsc?.topQueries ?? [];
  const titleFix = queries.filter((q) => q.position <= 10 && q.ctr < 2 && q.impressions >= 200)
    .sort((a, b) => b.impressions - a.impressions)[0];
  if (titleFix) {
    findings.push({
      tone: 'warn',
      title: 'Rewrite a title tag',
      body: `"${titleFix.query}" ranks #${titleFix.position.toFixed(0)} with ${titleFix.impressions.toLocaleString()} impressions but only ${titleFix.ctr.toFixed(1)}% CTR. A 5% CTR would be ${Math.round(titleFix.impressions * 0.05).toLocaleString()} clicks.`,
      metric: `${titleFix.impressions.toLocaleString()} impr`,
    });
  }

  // 5. Page-2 quick-win — needs one content push
  const quickWin = queries.filter((q) => q.position > 10 && q.position <= 20 && q.impressions >= 100)
    .sort((a, b) => b.impressions - a.impressions)[0];
  if (quickWin) {
    findings.push({
      tone: 'good',
      title: 'One push to page 1',
      body: `"${quickWin.query}" is at #${quickWin.position.toFixed(0)} with ${quickWin.impressions.toLocaleString()} impressions. One content refresh could move it to page 1.`,
      metric: `#${quickWin.position.toFixed(0)}`,
    });
  }

  return findings.slice(0, 5);
}

// ─── Page diagnoses ────────────────────────────────────────────────────────

export interface DiagnosedPage extends ContentPageRow {
  diagnosis: 'broken-funnel' | 'hidden-gem' | 'healthy' | 'low-engagement' | null;
  estimatedLeadsPerMonth: number;
}

export function diagnosePages(pages: ContentPageRow[], assumedConversionRate = 0.025): DiagnosedPage[] {
  return pages.map((p) => {
    let diagnosis: DiagnosedPage['diagnosis'] = null;
    if (p.sessions >= 500 && p.conversions === 0) diagnosis = 'broken-funnel';
    else if (p.engagementRate >= 80 && p.conversions === 0) diagnosis = 'hidden-gem';
    else if (p.sessions >= 200 && p.engagementRate < 35) diagnosis = 'low-engagement';
    else if (p.conversions > 0) diagnosis = 'healthy';
    const estimatedLeadsPerMonth = Math.round(p.sessions * assumedConversionRate);
    return { ...p, diagnosis, estimatedLeadsPerMonth };
  });
}

// ─── Channel-CPA leaderboard for KPIs ──────────────────────────────────────

export interface ChannelCpa {
  channel: string;
  cpa: number;
}

export function channelCpaList(d: AdsDashboard): ChannelCpa[] {
  const list: ChannelCpa[] = [];
  if (d.google_ads?.configured && d.google_ads.conversions > 0) {
    list.push({ channel: 'Google Ads', cpa: d.google_ads.spend / d.google_ads.conversions });
  }
  if (d.meta_ads?.configured && d.meta_ads.conversions > 0) {
    list.push({ channel: 'Meta Ads', cpa: d.meta_ads.spend / d.meta_ads.conversions });
  }
  return list;
}

// ─── Channel group findings ────────────────────────────────────────────────

export function topChannelsBySessions(rows: ChannelGroupRow[]): ChannelGroupRow[] {
  return [...rows].sort((a, b) => b.sessions - a.sessions);
}

// ─── Formatters ────────────────────────────────────────────────────────────

/**
 * Currency the connected ad accounts report in (CommandCenter.currency).
 * Set once per render by AdsDashboardPage; USD when unknown or mixed.
 */
let reportCurrency = 'USD';
export function setReportCurrency(code: string | null | undefined): void {
  reportCurrency = code || 'USD';
}

export function formatMoney(n: number): string {
  if (!isFinite(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: reportCurrency, minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function formatNumber(n: number, decimals = 0): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}
